/**
 * 数据大屏 - 聚合统计接口
 *
 * 设计原则：绝不让前端拉全表数据后再聚合。
 * 后端需提供以下聚合端点，在数据库层完成 COUNT / SUM / GROUP BY / COUNT DISTINCT。
 *
 * 如果后端尚未实现这些聚合端点：
 *   - 计数类：使用 pageSize=1 读取 total（仅拉 1 行）
 *   - 求和/分组类：需后端提供聚合接口，前端无法替代
 *   - 去重类：需后端提供 count distinct，前端无法替代
 */

import { get } from '@/utils/http'

/** 安全包装：接口不存在时不抛异常，返回默认值 */
async function safeGet(url, params = {}) {
  try {
    return await get(url, params)
  } catch (e) {
    return null
  }
}

// ============================================================
//  广告记录聚合（建议后端实现为 /admin/adRecord/statistics）
//  参数：startTime, endTime
//  返回：{ recordCount, totalEcpm, activeUserCount }
//  说明：后端执行 SELECT COUNT(*), SUM(ecpm), COUNT(DISTINCT user_id)
//        一次查询替代前端三次拉全表，性能提升数百倍
// ============================================================
export async function getAdRecordStats(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/statistics', { startTime, endTime })
  if (result && result.data) {
    return {
      recordCount: result.data.recordCount || result.data.count || 0,
      totalEcpm: result.data.totalEcpm || result.data.sum || 0,
      activeUserCount: result.data.activeUserCount || result.data.userCount || 0
    }
  }
  return { recordCount: 0, totalEcpm: 0, activeUserCount: 0 }
}

// ============================================================
//  广告记录按小时聚合（建议后端实现为 /admin/adRecord/hourlyRevenue）
//  参数：startTime, endTime
//  返回：[{ hour: "2026-06-18 09", total_ecpm: 123 }, ...]
//  说明：SELECT DATE_FORMAT(created_time,'%Y-%m-%d %H') AS hour, SUM(ecpm)
//        GROUP BY hour ORDER BY hour
// ============================================================
export async function getHourlyRevenue(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/hourlyRevenue', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data.list) ? result.data.list
      : Array.isArray(result.data) ? result.data : []
    return list
  }
  return []
}

// ============================================================
//  广告记录按天聚合（建议后端实现为 /admin/adRecord/dailyRevenue）
//  参数：startTime, endTime
//  返回：[{ date: "2026-06-12", total_ecpm: 1234 }, ...]
//  说明：SELECT DATE(created_time) AS date, SUM(ecpm)
//        GROUP BY date ORDER BY date
// ============================================================
export async function getDailyRevenue(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/dailyRevenue', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data.list) ? result.data.list
      : Array.isArray(result.data) ? result.data : []
    return list
  }
  return []
}

// ============================================================
//  广告按平台汇总收益（/admin/adRecord/platformSummary）
//  参数：startTime, endTime
//  返回：{ totalRevenue, totalCount, items: [{ adnName, ecpm, percent }] }
//  说明：一次请求替代前端拉全表再 GROUP BY adnName + SUM
// ============================================================
export async function getAdPlatformSummary(startTime, endTime, extraParams = {}) {
  const result = await safeGet('/admin/adRecord/platformSummary', { startTime, endTime, ...extraParams })
  if (result && result.data) {
    return {
      totalRevenue: result.data.totalRevenue || 0,
      totalCount: result.data.totalCount || 0,
      items: Array.isArray(result.data.items) ? result.data.items : []
    }
  }
  return { totalRevenue: 0, totalCount: 0, items: [] }
}

// ============================================================
//  广告记录计数（仅计数，pageSize=1 只拉 1 行，用 total 字段）
//  参数：startTime, endTime
//  说明：即使后端只有分页接口，pageSize=1 + total 也比拉全表快几千倍
// ============================================================
export async function getAdRecordCount(startTime, endTime) {
  const result = await safeGet('/admin/adRecord/page', {
    page: 1,
    pageSize: 1,
    startTime,
    endTime
  })
  if (result && result.data) {
    return result.data.total || 0
  }
  return 0
}

// ============================================================
//  用户统计（建议后端实现为 /admin/users/statistics）
//  返回：{ totalUsers, teamLeaderCount }
//  说明：totalUsers 可直接从分页接口 total 获取
//        teamLeaderCount 需要 SELECT COUNT(*) WHERE role != 0
// ============================================================
export async function getUserStats() {
  const result = await safeGet('/admin/dashboard/users/statistics')
  if (result && result.data) {
    return {
      totalUsers: result.data.totalUsers || result.data.userCount || 0,
      teamLeaderCount: result.data.teamLeaderCount || result.data.leaderCount || 0
    }
  }
  return { totalUsers: 0, teamLeaderCount: 0 }
}

// ============================================================
//  提现统计（建议后端实现为 /admin/userWithdrawalRecords/statistics）
//  参数：status=1（已打款）
//        date（今日日期，可选）
//  返回：{ totalAmount, todayAmount }
//  说明：SELECT SUM(amount) FROM withdrawal WHERE status=1 [AND date=xxx]
//        pageSize=99999 拉全表绝对不可取！
// ============================================================
export async function getWithdrawalStats(date) {
  const params = { status: 1 }
  if (date) params.date = date
  const result = await safeGet('/admin/dashboard/userWithdrawalRecords/statistics', params)
  if (result && result.data) {
    return {
      totalAmount: parseFloat(result.data.totalAmount || result.data.total || 0),
      todayAmount: parseFloat(result.data.todayAmount || result.data.today || 0)
    }
  }
  return { totalAmount: 0, todayAmount: 0 }
}





// ============================================================
//  用户总数（简单的 total 读取，只拉 1 行）
// ============================================================
export async function getTotalUserCount() {
  const result = await safeGet('/admin/users', { page: 1, pageSize: 1 })
  if (result && result.data) {
    return result.data.total || 0
  }
  return 0
}

// ============================================================
//  广告平台列表（GET /admin/adRecord/platforms）
//  后端执行 SELECT DISTINCT adn_name FROM ad_record
//  返回：["pangle", "admob", "gdt", ...]
// ============================================================
export async function getAdPlatforms() {
  const result = await safeGet('/admin/adRecord/platforms')
  if (result && result.data) {
    return Array.isArray(result.data) ? result.data : []
  }
  return []
}
